/**
 * Node views for math: inline formulas, display formulas and macro definitions, edited in place
 * with the LyX-style math editor (editor/lyxmath). Formulas render statically first and become
 * editable fields when they come into view or are hovered/clicked.
 */
import type { Node as PMNode } from 'prosemirror-model';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import { dragFromAtom, shiftClickAt } from '../plugins/dragselect';
import type { Decoration, EditorView, NodeView } from 'prosemirror-view';
import { macroFromLyxLines, parseFormula, renderHullSource, numberedType, type HullType } from '@overlyx/core';
import { LyxMathField, renderStaticInto, activeMathField, rowRectsOf, mathCursorListeners, mathFocusListeners, type PeerCaret } from '../lyxmath/field';
import { addInnerCursorSource, peerCursorsOf } from '../collab';
import { onMathRendererChange } from '../lyxmath/mathjax';
import { macroTableFor, mathViews, macroVersion, macrosReady } from '../lyxmath/macrotable';
import { showContextMenu, type MenuItem } from '../contextmenu';
import { clipboardMenuItems, selectionCovers } from '../clipmenu';
import { formulaLinkItems } from '../permalink';
import { toggleMathDisplay, countLabelRefs, renameLabelRefs } from '../commands';
import { editorContext, viewDocDir, viewProject } from '../context';
import { getPrefs } from '../../prefs';
import { documentViewScale } from '../viewscale';
import { wideRoom, onWideWidth } from '../widecontent';
import { openRewriteMath, REWRITE_KEY } from '../ai/rewrite';
import { openMathLinkBox, openLink, mathLinkUrl, LINK_KEY } from '../links';
import { applyChangeAttrs } from '../plugins/changes';
import { FormulaReview } from '../mathconflict';
import { FORMULA_EDIT } from '../plugins/undogroups';
import { formulaNodeGrid, convertFormulaToTable } from '../formulatable';

/** Position of a formula that was just inserted by the user and should grab the keyboard once mounted. */
export const pendingFocus: { pos: number | null; keys: string[]; /** the formula was opened by typing $ / $$ (see LyxMathField.dollar) */ dollar: '' | '$' | '$$'; /** opened from the toolbar or a menu: Space is a TeX space (commands.ts insertMathFromUi) */ texSpaces?: boolean } = { pos: null, keys: [], dollar: '' };

/* ------------------------------------------------ deferred static rendering */

interface Deferrable { dom: HTMLElement; view: EditorView; pending: boolean; stale: boolean; renderPending(): void }
/** formulas showing their source instead of a rendering; rendered in idle time (document order) or when scrolled near */
const staticQueue = new Set<Deferrable>();
let staticPumpScheduled = false;
let batchStart = 0, lastBudgetCheck = 0;
/**
 * Synchronous rendering budget: one burst of node-view constructions (the initial render of a
 * document) may spend ~40 ms on MathJax; the formulas after that only show their source and are
 * rendered in idle time. Keeps the first paint of a long paper fast without the formulas at the
 * top (where the cursor is) ever appearing unrendered.
 */
function canRenderNow(): boolean {
  const now = performance.now();
  if (now - lastBudgetCheck > 20) batchStart = now;
  lastBudgetCheck = now;
  return now - batchStart < 40;
}
/**
 * requestIdleCallback — or, where there is none (Safari), a timer whose deadline really runs out: the
 * pump renders formulas while `timeRemaining()` lasts, and a constant 8 ms had it render every queued
 * formula of a long paper in one go, which froze Safari for seconds after the paper opened (no
 * typing, no clicks, no timers; ~30 s for recurrent_feature in Playwright's WebKit).
 */
export function idleCallback(cb: (d: { timeRemaining(): number }) => void, w: { requestIdleCallback?: unknown } = window as never): void {
  if (typeof w.requestIdleCallback === 'function') { (w.requestIdleCallback as (cb: unknown, o: unknown) => void)(cb, { timeout: 500 }); return; }
  setTimeout(() => { const end = performance.now() + 8; cb({ timeRemaining: () => Math.max(0, end - performance.now()) }); }, 16);
}
function schedulePump(): void {
  if (staticPumpScheduled || !staticQueue.size) return;
  staticPumpScheduled = true;
  idleCallback(pumpStatic);
}
function pumpStatic(deadline: { timeRemaining(): number }): void {
  staticPumpScheduled = false;
  let waiting = false;
  for (const v of staticQueue) {
    if (deadline.timeRemaining() < 3) { waiting = true; break; }
    if (!v.pending && !v.stale) { staticQueue.delete(v); continue; }
    if (!macrosReady(v.view)) continue;     // stays queued until its document's macros are known
    v.renderPending();
  }
  if (waiting) schedulePump();
}

/* ------------------------------------------------ lazy upgrade of static formulas */

interface Upgradable extends Deferrable { upgrade(): void }
const lazyQueue: Upgradable[] = [];
let pumping = false;
const io = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver((entries) => {
  for (const e of entries) {
    if (!e.isIntersecting) continue;
    const v = (e.target as any).__lyxMathView as Upgradable | undefined;
    if (!v) continue;
    // near the viewport: render right away instead of waiting for idle time
    if (v.pending && macrosReady(v.view)) v.renderPending();
    if (!lazyQueue.includes(v)) lazyQueue.push(v);
  }
  pump();
}, { rootMargin: '900px 0px' }) : null;

function pump() {
  if (pumping) return;
  pumping = true;
  const step = () => {
    const batch = lazyQueue.splice(0, 8);
    // upgrading parses the formula — a field built before the document's macros are known parses
    // them wrong (and used to stay wrong): keep those queued until the metadata has arrived
    const deferred = batch.filter(v => !macrosReady(v.view));
    for (const v of batch) if (!deferred.includes(v)) v.upgrade();
    lazyQueue.push(...deferred);
    const progressed = deferred.length < batch.length;
    if (lazyQueue.length && progressed) requestAnimationFrame(step); else pumping = false;
  };
  requestAnimationFrame(step);
}
function watchLazy(v: Upgradable) { (v.dom as any).__lyxMathView = v; io?.observe(v.dom); }
function unwatchLazy(v: Upgradable) { io?.unobserve(v.dom); staticQueue.delete(v); const i = lazyQueue.indexOf(v); if (i >= 0) lazyQueue.splice(i, 1); }

/**
 * Another math font: fields draw themselves again; static formulas near the viewport are drawn at
 * once (their old markup has lost its font's CSS), the others in idle time, in document order.
 */
onMathRendererChange(() => {
  for (const v of mathViews) (v as { markStale?(): void }).markStale?.();
  if (typeof window !== 'undefined') {
    const h = window.innerHeight;
    // measured first, rendered after: one layout, not one per formula
    const near = [...staticQueue].filter(v => { if (!v.stale) return false; const r = v.dom.getBoundingClientRect(); return r.height > 0 && r.bottom > -900 && r.top < h + 900; });
    for (const v of near) if (macrosReady(v.view)) v.renderPending();
  }
  schedulePump();
});

/* ------------------------------------------------ cursors of others in formulas */

/** where each field's formula is (its editor and position) */
const fieldPlaces = new WeakMap<LyxMathField, { view: EditorView; pos: () => number | undefined }>();

/**
 * While a formula has the keyboard, the cursor shared with the other editors of the document is the
 * formula's place and the cursor inside it (editor/collab.ts); theirs come back as decorations
 * on the formula, which its field draws (peerCursorsOf).
 */
addInnerCursorSource({
  local(view) {
    const f = activeMathField();
    const place = f && fieldPlaces.get(f);
    if (!f || !place || place.view !== view) return null;
    const pos = place.pos();
    return pos === undefined ? null : { pos, cursor: f.cursorData() };
  },
  subscribe(cb) {
    const f = () => cb();
    mathCursorListeners.add(f); mathFocusListeners.add(f);
    return () => { mathCursorListeners.delete(f); mathFocusListeners.delete(f); };
  },
});

/* ------------------------------------------------ shared */

const DELIMS: [string, string, string][] = [['( )', '(', ')'], ['[ ]', '[', ']'], ['{ }', '{', '}'], ['| |', '|', '|'], ['‖ ‖', 'Vert', 'Vert'], ['⟨ ⟩', 'langle', 'rangle'], ['⌊ ⌋', 'lfloor', 'rfloor'], ['⌈ ⌉', 'lceil', 'rceil']];

/** Menu entries shared by all formula kinds. */
function commonMathMenu(f: LyxMathField): MenuItem[] {
  const ins = (latex: string) => () => { f.focus(); f.execute('insert', latex); };
  const c = f.cursor;
  const link = c.selection ? null : f.linkAtCursor();
  const url = link ? mathLinkUrl(link.target) : '';
  return [
    ...(link ? [
      { label: url, info: true },
      { label: 'Open link', icon: 'open', shortcut: (/Mac/.test(navigator.platform) ? '⌘' : 'Ctrl') + '+click', action: () => openLink(url) },
      { label: 'Edit link…', icon: 'edit', shortcut: LINK_KEY, action: () => openMathLinkBox(f) },
      { label: 'Copy link', icon: 'copy', action: () => { void navigator.clipboard?.writeText(url); } },
      { label: 'Remove link', icon: 'unlink', action: () => { f.execute('unlink'); } },
    ] as MenuItem[] : [{ label: 'Insert link', icon: 'link', shortcut: LINK_KEY, action: () => openMathLinkBox(f) } as MenuItem]),
    ...(getPrefs().aiRewrite ? [{ label: c.selection ? 'Rewrite selected part with AI…' : 'Rewrite formula with AI…', icon: 'ai', shortcut: REWRITE_KEY, action: () => openRewriteMath(f) } as MenuItem] : []),
    { sep: true },
    { label: 'Insert', icon: 'insert', sub: [
      { label: 'Fraction', shortcut: 'Alt+M F', action: ins('\\frac{#0}{}') },
      { label: 'Square root', shortcut: 'Alt+M S', action: ins('\\sqrt{#0}') },
      { label: 'Root', shortcut: 'Alt+M R', action: ins('\\sqrt[]{#0}') },
      { label: 'Sum', shortcut: 'Alt+M U', action: ins('\\sum') },
      { label: 'Integral', shortcut: 'Alt+M I', action: ins('\\int') },
      { label: 'Limit', shortcut: 'Alt+M L', action: ins('\\lim') },
      { label: c.mode === 'text' ? 'Math inside the text' : 'Text', shortcut: 'Ctrl+M', action: () => { f.focus(); f.execute('text'); } },
      { label: 'Superscript', shortcut: 'Alt+M E', action: () => { f.focus(); f.execute('moveToSuperscript'); } },
      { label: 'Subscript', shortcut: 'Alt+M X', action: () => { f.focus(); f.execute('moveToSubscript'); } },
      { label: 'Delimiters', sub: DELIMS.map(([l, a, b]) => ({ label: l, action: () => { f.focus(); f.execute('delim', a, b); } })) },
      { label: 'Matrix', sub: [['2×2', 2, 2, 'pmatrix'], ['3×3', 3, 3, 'pmatrix'], ['2×2 brackets', 2, 2, 'bmatrix'], ['cases', 2, 2, 'cases']].map(([l, r, cc, env]) => ({ label: String(l), action: () => { f.focus(); f.execute('matrix', r, cc, env); } })) },
      { label: 'Thin space', shortcut: 'Ctrl+Space', action: ins('\\,') },
    ] },
    { label: 'Font', icon: 'format', sub: [['Roman', 'mathrm'], ['Bold', 'mathbf'], ['Bold symbol', 'boldsymbol'], ['Calligraphic', 'mathcal'], ['Blackboard', 'mathbb'], ['Fraktur', 'mathfrak'], ['Sans serif', 'mathsf'], ['Typewriter', 'mathtt'], ['Italic', 'mathit']].map(([l, n]) => ({ label: l, action: () => { f.focus(); f.execute('font', n); } })) },
    { label: 'Toggle limits (\\limits)', action: () => { f.focus(); f.execute('limits'); } },
    { label: c.selection ? 'Copy LaTeX of selection' : 'Copy LaTeX', icon: 'copy', action: () => { void navigator.clipboard?.writeText(c.selection ? c.grabSelection() : f.latex); } },
  ];
}

/** a formula that is one matrix / array made a real table, whose text can wrap (formulatable.ts) */
function toTable(view: EditorView, getPos: () => number | undefined): void {
  const pos = getPos();
  if (pos !== undefined) void convertFormulaToTable(view, pos, macroTableFor(view, pos).table);
}
/** "Convert to table" in the menu of a formula that is a table */
function toTableItems(view: EditorView, getPos: () => number | undefined): MenuItem[] {
  const pos = getPos();
  const node = pos === undefined ? null : view.state.doc.nodeAt(pos);
  if (!node || !formulaNodeGrid(node, macroTableFor(view, pos).table)) return [];
  return [{ label: 'Convert to table (its text can wrap)', icon: 'table', action: () => toTable(view, getPos) }];
}

function deleteFormula(view: EditorView, getPos: () => number | undefined) {
  const pos = getPos();
  if (pos === undefined) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize));
  view.focus();
}

/**
 * Leave the field into the document: cursor before/after the formula node. `dissolve` (an empty
 * formula left with a horizontal cursor move, as LyX does): the formula is removed and the cursor
 * takes its place.
 */
function moveOut(view: EditorView, getPos: () => number | undefined, dir: string, insertSpace: boolean, dissolve = false, putBack = '') {
  const pos = getPos();
  if (pos === undefined) return;
  const node = view.state.doc.nodeAt(pos);
  const size = node ? node.nodeSize : 1;
  const back = dir === 'backward' || dir === 'upward';
  let tr = view.state.tr;
  if (dissolve && node && putBack) {
    // Backspace in the empty formula that `$` / `$$` opened: the typed dollars come back as text
    tr = tr.replaceWith(pos, pos + size, view.state.schema.text(putBack));
    tr = tr.setSelection(TextSelection.create(tr.doc, pos + putBack.length));
  }
  else if (dissolve && node) { tr = tr.delete(pos, pos + size); tr = tr.setSelection(TextSelection.near(tr.doc.resolve(pos), back ? -1 : 1)); }
  else tr = tr.setSelection(TextSelection.near(view.state.doc.resolve(back ? pos : pos + size), back ? -1 : 1));
  if (insertSpace) tr = tr.insertText(' ');
  view.dispatch(tr);
  view.focus();
}

/** Shift+arrow hit the formula's edge: the selection continues in the document with the formula taken whole. */
function selectOutOf(view: EditorView, getPos: () => number | undefined, dir: string) {
  const pos = getPos();
  if (pos === undefined) return;
  const node = view.state.doc.nodeAt(pos);
  const size = node ? node.nodeSize : 1;
  const back = dir === 'backward' || dir === 'upward';
  view.focus();   // focus first: on focus ProseMirror re-reads the DOM selection and would clobber one dispatched before it
  try { view.dispatch(view.state.tr.setSelection(TextSelection.between(view.state.doc.resolve(back ? pos + size : pos), view.state.doc.resolve(back ? pos : pos + size))).scrollIntoView()); } catch { /* gone */ }
}

/**
 * A drag left the formula: continue as a document drag with the formula selected whole (LyX's
 * undispatched mouse motion). When the pointer comes back into the formula the field resumes its
 * own drag (`reenter`) and the document selection collapses onto the formula.
 */
function dragOutOf(view: EditorView, getPos: () => number | undefined, ev: MouseEvent, reenter: (ev: MouseEvent) => boolean) {
  const pos = getPos();
  if (pos === undefined) return;
  const node = view.state.doc.nodeAt(pos);
  dragFromAtom(view, pos, pos + (node ? node.nodeSize : 1), ev, mv => {
    if (!reenter(mv)) return false;
    const p = getPos();
    if (p !== undefined) { try { view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(p)))); } catch { /* gone */ } }
    return true;
  });
}

/**
 * The selection's Cut / Copy / Paste (and the formula's LaTeX) when the formula at getPos lies inside the
 * document's selection — an equation selected whole, text dragged across it — else null.
 */
function selectedFormulaItems(view: EditorView, getPos: () => number | undefined, f: LyxMathField): MenuItem[] | null {
  const pos = getPos();
  if (pos === undefined) return null;
  const node = view.state.doc.nodeAt(pos);
  if (!node || !selectionCovers(view.state.selection, pos, node.nodeSize)) return null;
  return [...clipboardMenuItems(view), { label: 'Copy LaTeX', icon: 'copy', action: () => { void navigator.clipboard?.writeText(f.latex); } }];
}

/**
 * Common field wiring: context menu, mouse isolation from ProseMirror, keyboard passthrough. The menu
 * shows the formula's entries and then either the field's editing entries (insert, font, …) or —
 * while the field has no cursor and the formula lies inside the document's selection (`selected`) —
 * that selection's Cut / Copy / Paste, so a selected equation can be cut from a right-click on it.
 */
function wire(f: LyxMathField, menu: () => MenuItem[], selected?: () => MenuItem[] | null) {
  f.dom.addEventListener('contextmenu', (ev: MouseEvent) => {
    ev.preventDefault(); ev.stopPropagation();
    const sel = !f.hasFocus() && selected ? selected() : null;
    showContextMenu(ev.clientX, ev.clientY, [...menu(), { sep: true }, ...(sel ?? commonMathMenu(f))]);
  });
  f.dom.addEventListener('mousedown', ev => { ev.stopPropagation(); });
  f.dom.addEventListener('keydown', (ev: KeyboardEvent) => {
    // global shortcuts (save, find, …) may bubble to the editor; everything else stays in the field
    const mod = /Mac/.test(navigator.platform) ? ev.metaKey : ev.ctrlKey;
    const passthrough = mod && ['s', 'r', 'f', 'o', 'p', 'w'].includes(ev.key.toLowerCase()) && !ev.altKey;
    if (!passthrough) ev.stopPropagation();
  });
}

function focusIfPending(f: LyxMathField, getPos: () => number | undefined) {
  const pos = getPos();
  if (pos === undefined || pendingFocus.pos !== pos) return;
  pendingFocus.pos = null;
  if (pendingFocus.dollar) { f.dollar = pendingFocus.dollar; pendingFocus.dollar = ''; }
  if (pendingFocus.texSpaces) { f.typeAsTex(); pendingFocus.texSpaces = false; }
  // a formula made from selected text continues at its end; an empty one starts in its first cell (LyX: align, cases, ... begin left of the first &)
  requestAnimationFrame(() => { f.focus(f.isEmpty() ? 'start' : 'end'); for (const k of pendingFocus.keys.splice(0)) f.execute('insert', k); });
}

/* ------------------------------------------------ inline formulas */

export class MathInlineView implements NodeView {
  dom: HTMLElement;
  field: LyxMathField | null = null;
  private staticEl: HTMLElement | null = null;
  private staticKey = '';
  private updating = false;
  private lastLatex: string;
  private review: FormulaReview;
  /** shows its source; rendered later (idle time / scrolled near / macros known) */
  pending = false;
  /** rendered with a math font no longer chosen: rendered again in idle time */
  stale = false;
  private renderStamp = 0;
  private peers: PeerCaret[] = [];

  constructor(private node: PMNode, public view: EditorView, private getPos: () => number | undefined, decorations?: readonly Decoration[]) {
    this.dom = document.createElement('span');
    this.dom.className = 'lyx-math-inline';
    this.review = new FormulaReview(view, getPos, false, node.attrs.editClock);
    this.lastLatex = String(node.attrs.latex);
    this.dom.classList.toggle('empty', !this.lastLatex.trim());
    applyChangeAttrs(this.dom, node);
    mathViews.add(this);
    if (pendingFocus.pos !== null && pendingFocus.pos === getPos()) this.upgrade();
    else { this.renderStaticOrDefer(); watchLazy(this); }
    this.dom.addEventListener('pointerenter', () => this.upgrade());
    this.setPeers(peerCursorsOf(decorations));
  }

  /** others' cursors in this formula: drawn by its field */
  private setPeers(peers: PeerCaret[]) {
    if (!peers.length && !this.peers.length) return;
    this.peers = peers;
    if (peers.length) this.upgrade();
    this.field?.setPeers(peers);
  }

  private ensureStaticEl(): HTMLElement {
    if (!this.staticEl) { this.staticEl = document.createElement('span'); this.staticEl.className = 'lyx-math-static'; this.dom.replaceChildren(this.staticEl); }
    return this.staticEl;
  }
  private renderStaticOrDefer() {
    if (macrosReady(this.view) && canRenderNow()) this.renderStatic();
    else this.showPlaceholder();
  }
  private showPlaceholder() {
    const el = this.ensureStaticEl();
    el.classList.add('pending');
    el.textContent = this.lastLatex;
    this.pending = true;
    staticQueue.add(this);
    schedulePump();
  }
  renderPending() {
    if (!this.pending && !this.stale) return;
    this.renderStatic();
  }
  markStale() { if (this.field || this.pending) return; this.stale = true; staticQueue.add(this); }
  private renderStatic() {
    const el = this.ensureStaticEl();
    this.pending = false; this.stale = false; staticQueue.delete(this); el.classList.remove('pending');
    const { key, table } = macroTableFor(this.view, this.getPos());
    const stamp = ++this.renderStamp;
    renderStaticInto(el, '$' + this.lastLatex + '$', false, table, { project: viewProject(this.view), docDir: viewDocDir(this.view) },
      () => { if (stamp === this.renderStamp && !this.field) this.renderStatic(); });
    this.staticKey = key;
  }

  upgrade() {
    if (this.field) return;
    unwatchLazy(this);
    this.pending = false; this.stale = false; this.renderStamp++;
    const { key, table } = macroTableFor(this.view, this.getPos());
    const f = new LyxMathField({
      latex: '$' + this.lastLatex + '$', display: false, macros: table,
      imageContext: { project: viewProject(this.view), docDir: viewDocDir(this.view) },
      onChange: latex => this.commit(latex),
      onBlur: () => {
        this.review.adopt(this.node.attrs.editClock);
        this.lastLatex = String(this.node.attrs.latex);
        if (this.field) { this.updating = true; this.field.setLatex('$' + this.lastLatex + '$'); this.updating = false; }
      },
      onMoveOut: (dir, o) => moveOut(this.view, this.getPos, dir, !!o.insertSpace, !!o.dissolve, o.putBack),
      onDragOut: (ev, reenter) => dragOutOf(this.view, this.getPos, ev, reenter),
      onShiftClick: ev => shiftClickAt(this.view, ev.clientX, ev.clientY),
      onSelectOut: dir => selectOutOf(this.view, this.getPos, dir),
      onCommand: k => {
        // `$$` typed: the (empty) inline formula becomes a display formula, the cursor stays inside
        if (k !== '$$') return;
        const pos = this.getPos();
        if (pos === undefined) return;
        pendingFocus.pos = pos; pendingFocus.keys = []; pendingFocus.dollar = '$$';
        this.selectSelf();
        toggleMathDisplay(this.view.state, this.view.dispatch);
        const focusNew = (): boolean => { const spec = ((this.view as any).nodeDOM(pos) as any)?.pmViewDesc?.spec; if (!spec?.focus) return false; spec.focus('start'); return true; };
        if (!focusNew()) requestAnimationFrame(() => focusNew());
      },
    });
    (f as any)._macroKey = key;
    (f as any)._toggleDisplay = () => { this.selectSelf(); toggleMathDisplay(this.view.state, this.view.dispatch); };
    (f as any)._toTable = () => toTable(this.view, this.getPos);
    this.field = f;
    fieldPlaces.set(f, { view: this.view, pos: this.getPos });
    if (this.peers.length) f.setPeers(this.peers);
    this.dom.replaceChildren(f.dom);
    this.staticEl = null;
    wire(f, () => this.formulaMenu(), () => selectedFormulaItems(this.view, this.getPos, f));
    focusIfPending(f, this.getPos);
  }
  ensureField(): LyxMathField { this.upgrade(); return this.field!; }
  /** the formula's own menu entries (also the editor's right-click menu on the formula's row, editormenu.ts) */
  formulaMenu(): MenuItem[] {
    return [
      { label: 'Inline formula', info: true },
      { label: 'Convert to display formula', icon: 'formula', action: () => { this.selectSelf(); toggleMathDisplay(this.view.state, this.view.dispatch); } },
      ...toTableItems(this.view, this.getPos),
      ...formulaLinkItems(this.view, this.getPos),
      { label: 'Delete formula', icon: 'delete', action: () => deleteFormula(this.view, this.getPos) },
    ];
  }

  private commit(latex: string) {
    if (this.updating) return;
    const body = latex.replace(/^\$|\$$/g, '').replace(/^ $/, '');
    if (body === this.lastLatex) return;
    const pos = this.getPos();
    if (pos === undefined) return;
    const cur = this.view.state.doc.nodeAt(pos);
    if (!cur || cur.attrs.latex === body) return;
    const editClock = this.review.edited(body);
    this.lastLatex = body;
    this.dom.classList.toggle('empty', !body.trim());
    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, undefined, { ...cur.attrs, latex: body, editClock }).setMeta('addToHistory', true).setMeta(FORMULA_EDIT, pos));
  }
  refreshMacros() {
    pump();   // macros may have just arrived: deferred lazy upgrades can proceed
    if (this.pending) { if (macrosReady(this.view)) schedulePump(); return; }
    const { key, table } = macroTableFor(this.view, this.getPos());
    if (this.field) this.field.setMacros(table, key);
    else if (this.staticKey !== key) this.renderStatic();
  }
  private selectSelf() { const pos = this.getPos(); if (pos !== undefined) this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, pos))); }
  update(node: PMNode, decorations?: readonly Decoration[]): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    applyChangeAttrs(this.dom, node);
    const latex = String(node.attrs.latex);
    this.review.received(latex, node.attrs.editClock, !!this.field?.hasFocus());
    this.setPeers(peerCursorsOf(decorations));
    if (this.field) {
      this.refreshMacros();
      if (latex !== this.lastLatex && !this.field.hasFocus()) {
        this.updating = true;
        this.lastLatex = latex;
        this.field.setLatex('$' + latex + '$');
        this.dom.classList.toggle('empty', !latex.trim());
        this.updating = false;
      }
    } else if (latex !== this.lastLatex) {
      this.lastLatex = latex;
      this.dom.classList.toggle('empty', !latex.trim());
      if (this.pending) this.showPlaceholder(); else this.renderStatic();
    }
    return true;
  }
  selectNode() { this.dom.classList.add('ProseMirror-selectednode'); }
  deselectNode() { this.dom.classList.remove('ProseMirror-selectednode'); }
  stopEvent(ev: Event) { return !!this.field && this.field.dom.contains(ev.target as Node); }
  ignoreMutation() { return true; }
  focus(where: 'start' | 'end' = 'end') { this.ensureField().focus(where); }
  destroy() { this.review.destroy(); mathViews.delete(this); unwatchLazy(this); this.field?.destroy(); }
}

/* ------------------------------------------------ display formulas */

const ENV_MENU: { env: HullType; label: string }[] = [
  { env: 'equation', label: 'equation' },
  { env: 'align', label: 'align' },
  { env: 'gather', label: 'gather' },
  { env: 'multline', label: 'multline' },
  { env: 'eqnarray', label: 'eqnarray' },
  { env: 'flalign', label: 'flalign' },
  { env: 'alignat', label: 'alignat' },
];

export class MathDisplayView implements NodeView {
  dom: HTMLElement;
  field: LyxMathField | null = null;
  private staticEl: HTMLElement | null = null;
  private staticKey = '';
  /** the room the formula was last drawn for before breaking into lines (em), see breakWidth */
  private drawnWidth: number | undefined;
  private breakable = false;
  private breakableFor: string | null = null;
  numberEl: HTMLElement;
  labelEl: HTMLElement;
  metaEl: HTMLElement;
  private updating = false;
  private lastLatex: string;
  private review: FormulaReview;
  private ro: ResizeObserver | null = null;
  private mo: MutationObserver | null = null;
  /** shows its source; rendered later (idle time / scrolled near / macros known) */
  pending = false;
  /** rendered with a math font no longer chosen: rendered again in idle time */
  stale = false;
  private renderStamp = 0;
  private peers: PeerCaret[] = [];

  constructor(private node: PMNode, public view: EditorView, private getPos: () => number | undefined, decorations?: readonly Decoration[]) {
    this.review = new FormulaReview(view, getPos, true, node.attrs.editClock);
    this.lastLatex = String(node.attrs.latex);
    this.dom = document.createElement('span');
    this.dom.className = 'lyx-math-display';
    applyChangeAttrs(this.dom, node);
    const left = document.createElement('span');
    left.className = 'eq-left';
    left.contentEditable = 'false';
    this.metaEl = document.createElement('span');
    this.metaEl.className = 'eq-meta';
    this.metaEl.contentEditable = 'false';
    this.numberEl = document.createElement('span');
    this.numberEl.className = 'eq-number';
    this.labelEl = document.createElement('span');
    this.labelEl.className = 'eq-labels';
    this.metaEl.append(this.numberEl);
    left.append(this.labelEl);   // the label chip on the left of the formula, the number on the right (as in the output)
    this.staticEl = document.createElement('span');
    this.staticEl.className = 'lyx-math-static display';
    this.dom.append(left, this.staticEl, this.metaEl);
    this.renderMeta();
    this.labelEl.addEventListener('mousedown', (ev) => { ev.preventDefault(); this.editLabel(); });
    // equation numbers come from the numbering plugin as a data-eqnum node decoration
    this.mo = new MutationObserver(() => this.syncNumber());
    this.mo.observe(this.dom, { attributes: true, attributeFilter: ['data-eqnum'] });
    this.syncNumber();
    // wide formulas: centre on the text column and let them overflow into the margins symmetrically
    this.ro = new ResizeObserver(() => this.scheduleRelayout());
    this.ro.observe(this.dom);
    // the ruler's second track (editor/widecontent.ts): how far a wide formula may reach
    this.offWide = onWideWidth(() => this.scheduleRelayout());
    mathViews.add(this);
    if (pendingFocus.pos !== null && pendingFocus.pos === getPos()) this.upgrade();
    else { this.renderStaticOrDefer(); watchLazy(this); }
    this.dom.addEventListener('pointerenter', () => this.upgrade());
    this.setPeers(peerCursorsOf(decorations));
  }

  /** others' cursors in this formula: drawn by its field */
  private setPeers(peers: PeerCaret[]) {
    if (!peers.length && !this.peers.length) return;
    this.peers = peers;
    if (peers.length) this.upgrade();
    this.field?.setPeers(peers);
  }

  private contentEl(): HTMLElement { return this.field?.dom ?? this.staticEl!; }

  private renderStaticOrDefer() {
    if (macrosReady(this.view) && canRenderNow()) this.renderStatic();
    else this.showPlaceholder();
  }
  private showPlaceholder() {
    if (!this.staticEl) return;
    this.staticEl.classList.add('pending');
    this.staticEl.textContent = this.lastLatex;
    this.pending = true;
    staticQueue.add(this);
    schedulePump();
  }
  renderPending() {
    if (!this.pending && !this.stale) return;
    this.renderStatic();
  }
  markStale() { if (this.field || this.pending) return; this.stale = true; staticQueue.add(this); }
  private renderStatic() {
    const el = this.staticEl;
    if (!el) return;
    this.pending = false; this.stale = false; staticQueue.delete(this); el.classList.remove('pending');
    const { key, table } = macroTableFor(this.view, this.getPos());
    this.dom.classList.toggle('empty', !this.lastLatex.trim());   // an empty formula keeps its one box visible (styles.css .lm-empty)
    const stamp = ++this.renderStamp;
    this.drawnWidth = this.breakWidth();
    renderStaticInto(el, this.lastLatex, true, table, { project: viewProject(this.view), docDir: viewDocDir(this.view) },
      () => { if (stamp === this.renderStamp && this.staticEl === el) this.renderStatic(); }, this.drawnWidth);
    this.staticKey = key;
    this.scheduleRelayout();
  }

  upgrade() {
    if (this.field) return;
    unwatchLazy(this);
    this.pending = false; this.stale = false; this.renderStamp++;
    const { key, table } = macroTableFor(this.view, this.getPos());
    const f = new LyxMathField({
      latex: this.lastLatex, display: true, macros: table, width: (this.drawnWidth = this.breakWidth()),
      imageContext: { project: viewProject(this.view), docDir: viewDocDir(this.view) },
      onChange: latex => this.commit(latex),
      onBlur: () => {
        this.review.adopt(this.node.attrs.editClock);
        this.lastLatex = String(this.node.attrs.latex);
        if (this.field) { this.updating = true; this.field.setLatex(this.lastLatex); this.updating = false; }
      },
      onMoveOut: (dir, o) => moveOut(this.view, this.getPos, dir, !!o.insertSpace, !!o.dissolve, o.putBack),
      onDragOut: (ev, reenter) => dragOutOf(this.view, this.getPos, ev, reenter),
      onShiftClick: ev => shiftClickAt(this.view, ev.clientX, ev.clientY),
      onSelectOut: dir => selectOutOf(this.view, this.getPos, dir),
      onCommand: key => { if (key === 'n') this.toggleNumbering(); },
    });
    (f as any)._macroKey = key;
    (f as any)._toggleDisplay = () => { this.selectSelf(); toggleMathDisplay(this.view.state, this.view.dispatch); };
    (f as any)._toTable = () => toTable(this.view, this.getPos);
    this.field = f;
    fieldPlaces.set(f, { view: this.view, pos: this.getPos });
    if (this.peers.length) f.setPeers(this.peers);
    if (this.staticEl) { this.staticEl.replaceWith(f.dom); this.staticEl = null; } else this.dom.insertBefore(f.dom, this.metaEl);
    this.ro?.observe(f.dom);
    wire(f, () => this.formulaMenu(), () => selectedFormulaItems(this.view, this.getPos, f));
    focusIfPending(f, this.getPos);
  }
  ensureField(): LyxMathField { this.upgrade(); return this.field!; }

  refreshMacros() {
    pump();   // macros may have just arrived: deferred lazy upgrades can proceed
    if (this.pending) { if (macrosReady(this.view)) schedulePump(); return; }
    const { key, table } = macroTableFor(this.view, this.getPos());
    if (this.field) this.field.setMacros(table, key);
    else if (this.staticKey !== key) this.renderStatic();
  }

  private syncNumber() {
    const n = this.dom.getAttribute('data-eqnum') ?? '';
    if (this.numberEl.dataset.nums !== n) { this.numberEl.dataset.nums = n; this.numberEl.textContent = n; }
    this.numberEl.style.display = n ? '' : 'none';
    this.scheduleRelayout();
  }

  /** put each equation number at the vertical centre of its row (LyX draws them per row) */
  private layoutNumbers() {
    const nums = (this.numberEl.dataset.nums ?? '').split('\n').filter(Boolean);
    const h = this.hull();
    const numberedRowsIdx = h.type === 'multline' ? [h.rows.length - 1] : h.numberedRows.map((v, i) => (v === true ? i : -1)).filter(i => i >= 0);
    if (nums.length < 2 || numberedRowsIdx.length !== nums.length) { this.numberEl.classList.remove('per-row'); if (this.numberEl.textContent !== nums.join('\n')) this.numberEl.textContent = nums.join('\n'); return; }
    const container = this.field?.dom ?? this.staticEl;
    if (!container) return;
    const refs = this.field ? this.field.cellRefs() : renderHullSource(h, macroTableFor(this.view, this.getPos()).table).cells;
    const rects = rowRectsOf(h, refs, container);
    const base = this.dom.getBoundingClientRect();
    this.numberEl.classList.add('per-row');
    this.numberEl.replaceChildren(...nums.map((t, k) => {
      const r = rects[numberedRowsIdx[k]];
      const sp = document.createElement('span');
      sp.textContent = t;
      sp.style.top = `${((r.top + r.bottom) / 2 - base.top) / documentViewScale(this.dom)}px`;
      return sp;
    }));
  }

  private relayoutRaf = 0;
  private offWide: (() => void) | null = null;
  private scheduleRelayout() { cancelAnimationFrame(this.relayoutRaf); this.relayoutRaf = requestAnimationFrame(() => this.relayout()); }

  /** Wide formulas: size the middle grid track to the content and centre the whole block on the text
   *  column, letting it overflow symmetrically into the margins — as far as the ruler's second track
   *  allows (editor/widecontent.ts), never past the page's left edge. */
  private relayout() {
    const dom = this.dom;
    const scroll = dom.closest('.editor-scroll') as HTMLElement | null;
    if (!scroll || !dom.isConnected) return;
    // the column (or the equation number) changed width: a long formula breaks anew
    const w = this.breakWidth();
    if (w !== this.drawnWidth && !this.pending) {
      this.drawnWidth = w;
      if (this.field) this.field.setWidth(w);
      else if (this.staticEl) { this.renderStatic(); return; }
    }
    const content = this.contentEl();
    const contentW = Math.ceil(content.scrollWidth + 8);
    const shifted = dom.style.gridTemplateColumns !== '';
    const avail = shifted ? dom.parentElement!.clientWidth : dom.clientWidth;
    const metaW = this.metaEl.offsetWidth;
    const need = contentW + metaW + 10;
    this.layoutNumbers();
    const scale = documentViewScale(dom);
    const pageLeft = (scroll.getBoundingClientRect().left + 6) / scale;
    const left = (dom.getBoundingClientRect().left + scroll.scrollLeft) / scale - (parseFloat(dom.style.marginLeft) || 0);
    const leftRoom = Math.max(0, left - pageLeft);
    if (need <= avail) {
      if (shifted) { dom.style.marginLeft = ''; dom.style.width = ''; dom.style.gridTemplateColumns = ''; }
      this.placeLabel(leftRoom, content);
      return;
    }
    const shift = Math.round(Math.min(leftRoom, (need - avail) / 2, Math.max(0, (wideRoom(dom, avail) - avail) / 2)));
    dom.style.gridTemplateColumns = `0 ${contentW}px auto`;
    dom.style.marginLeft = shift > 0 ? `-${shift}px` : '';
    dom.style.width = shift > 0 ? `calc(100% + ${shift}px)` : '';
    this.placeLabel(leftRoom - shift, content);
  }

  /**
   * Where the label chip goes (data-label-pos, see the CSS): in the page margin left of the block
   * when there is room for it, else in the empty left cell beside a narrow formula, else above
   * the formula's left corner — never over the formula itself.
   */
  private placeLabel(marginRoom: number, content: HTMLElement) {
    const chipW = this.labelEl.offsetWidth;
    if (!chipW) { delete this.dom.dataset.labelPos; return; }
    const inside = (content.getBoundingClientRect().left - this.dom.getBoundingClientRect().left) / documentViewScale(this.dom);
    const pos = marginRoom >= chipW + 12 ? 'margin' : inside >= chipW + 8 ? 'inside' : 'above';
    if (this.dom.dataset.labelPos !== pos) this.dom.dataset.labelPos = pos;
  }

  private hull() { return this.field ? this.field.hull : parseFormula(this.lastLatex, macroTableFor(this.view, this.getPos()).table); }

  /**
   * The room a one-line formula has before MathJax breaks it into lines, in em of its font: the
   * room for wide content (the ruler's second track, at least the text column: widecontent.ts), less
   * room for the equation number on both sides (the formula stays centred).
   * Formulas of several rows (align, gather …) and cells with a grid are laid out as they are.
   */
  private breakWidth(): number | undefined {
    const latex = this.field?.latex ?? this.lastLatex;
    if (this.breakableFor !== latex) { const h = this.hull(); this.breakable = h.rows.length === 1 && h.ncols === 1; this.breakableFor = latex; }
    if (!this.breakable || !this.dom.isConnected) return undefined;
    const avail = this.dom.style.gridTemplateColumns ? this.dom.parentElement!.clientWidth : this.dom.clientWidth;
    if (!avail) return undefined;
    const room = wideRoom(this.dom, avail) - 2 * (this.metaEl.offsetWidth + 14) - 8;
    const box = (this.field?.dom ?? this.staticEl ?? this.dom).querySelector('mjx-container') ?? this.field?.dom ?? this.staticEl ?? this.dom;
    const fs = parseFloat(getComputedStyle(box).fontSize) * (box.tagName === 'MJX-CONTAINER' ? 1 : (parseFloat(getComputedStyle(box).getPropertyValue('--math-scale')) || 1));
    if (!fs) return undefined;
    // in half ems: a few pixels more or less draw the formula anew only when that changes where it breaks
    return Math.max(10, Math.floor((room / fs) * 2) / 2);
  }

  /** the formula's own menu entries (also the editor's right-click menu on the formula's row, editormenu.ts) */
  formulaMenu(): MenuItem[] {
    const h = this.hull();
    const numbered = numberedType(h);
    const labels = h.labels.filter(Boolean) as string[];
    return [
      { label: 'Display formula', info: true },
      { label: 'Numbered', shortcut: 'Alt+M N', checked: numbered, action: () => this.toggleNumbering() },
      { label: 'Number this line', checked: h.rows.length > 1 && !!h.numberedRows[this.currentRow()], disabled: h.rows.length < 2, action: () => this.ensureField().execute('numberLineToggle') },
      { label: labels.length ? `Edit label (${labels.join(', ')})…` : 'Add label…', icon: 'label', action: () => this.editLabel() },
      { label: 'Copy label name', icon: 'copy', disabled: !labels.length, action: () => { void navigator.clipboard?.writeText(labels[0] ?? ''); } },
      { label: 'Environment', icon: 'layout', sub: ENV_MENU.map(e => ({ label: e.label, checked: h.type === e.env, action: () => this.setEnv(e.env) })) },
      { label: 'New line (row)', icon: 'insert', shortcut: 'Enter', action: () => this.ensureField().execute('newline') },
      { label: 'Convert to inline formula', icon: 'formula', action: () => { this.selectSelf(); toggleMathDisplay(this.view.state, this.view.dispatch); } },
      ...toTableItems(this.view, this.getPos),
      ...formulaLinkItems(this.view, this.getPos),
      { label: 'Delete formula', icon: 'delete', action: () => deleteFormula(this.view, this.getPos) },
    ];
  }
  private currentRow(): number { const f = this.field; if (!f) return 0; const s = f.cursor.slices[0]; return Math.floor(s.idx / Math.max(1, f.hull.ncols)); }
  private selectSelf() { const pos = this.getPos(); if (pos !== undefined) this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, pos))); }

  private editLabel() {
    const h = this.hull();
    const cur = (h.labels.find(Boolean) ?? '') as string;
    editorContext.openDialog?.('label', {
      equation: true,
      initial: cur || 'eq:',
      hasLabel: !!cur,
      refCount: countLabelRefs(this.view, cur),
      onApply: (name: string) => { this.setLabel(name); if (cur && cur !== name) renameLabelRefs(this.view, cur, name); },
      onRemove: () => this.setLabel(''),
    });
  }

  private commit(fieldLatex: string) {
    if (this.updating) return;
    // the LyX writer adds the newlines around a display formula itself (core/lyx/writer.ts)
    const latex = fieldLatex.replace(/^\n/, '').replace(/\n$/, '');
    const pos = this.getPos();
    if (pos === undefined) return;
    const cur = this.view.state.doc.nodeAt(pos);
    if (!cur || cur.attrs.latex === latex) return;
    const editClock = this.review.edited(latex);
    this.lastLatex = latex;
    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, undefined, { ...cur.attrs, latex, editClock }).setMeta(FORMULA_EDIT, pos));
    this.renderMeta();
  }

  private renderMeta() {
    const h = this.hull();
    const numbered = numberedType(h);
    this.dom.classList.toggle('numbered', numbered);
    this.dom.dataset.env = h.type;
    const labels = h.labels.filter(Boolean) as string[];
    this.labelEl.textContent = labels.length ? labels.join(', ') : (numbered ? '+label' : '');
    this.labelEl.title = labels.length ? 'Label: ' + labels.join(', ') + ' (click to edit)' : 'Click to add a label';
    this.labelEl.style.display = numbered || labels.length ? '' : 'none';
    this.scheduleRelayout();   // the chip's width decides where it goes (placeLabel)
  }

  toggleNumbering() { this.ensureField().execute('numberToggle'); this.renderMeta(); }
  setEnv(env: HullType) { this.ensureField().execute('mutate', env); this.renderMeta(); }
  /**
   * The label chip names the formula as a whole: the label goes to the row that already carries
   * one, else to the cursor's row — unless that row has no number (\nonumber), in which case the
   * first numbered row takes it (a label on an unnumbered row would number it, undoing the choice).
   */
  setLabel(label: string) {
    const f = this.ensureField();
    const h = f.hull;
    let row = h.labels.findIndex(Boolean);
    if (row < 0) { row = this.currentRow(); if (h.numberedRows[row] === false) { const n = h.numberedRows.findIndex(x => x === true); if (n >= 0) row = n; } }
    f.execute('label', label, row);
    this.renderMeta();
  }

  update(node: PMNode, decorations?: readonly Decoration[]): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    applyChangeAttrs(this.dom, node);
    const latex = String(node.attrs.latex);
    this.review.received(latex, node.attrs.editClock, !!this.field?.hasFocus());
    this.setPeers(peerCursorsOf(decorations));
    if (this.field) this.refreshMacros();
    if (latex !== this.lastLatex) {
      this.lastLatex = latex;
      if (this.field) { if (!this.field.hasFocus()) { this.updating = true; this.field.setLatex(latex); this.updating = false; } }
      else if (this.pending) this.showPlaceholder();
      else this.renderStatic();
      this.renderMeta();
    }
    return true;
  }
  selectNode() { this.dom.classList.add('ProseMirror-selectednode'); }
  deselectNode() { this.dom.classList.remove('ProseMirror-selectednode'); }
  stopEvent(ev: Event) { return (!!this.field && this.field.dom.contains(ev.target as Node)) || this.metaEl.contains(ev.target as Node); }
  ignoreMutation() { return true; }
  focus(where: 'start' | 'end' = 'end') { this.ensureField().focus(where); }
  destroy() { this.review.destroy(); mathViews.delete(this); unwatchLazy(this); cancelAnimationFrame(this.relayoutRaf); this.offWide?.(); this.ro?.disconnect(); this.mo?.disconnect(); this.field?.destroy(); }
}

/* ------------------------------------------------ macro definitions */

/** FormulaMacro inset: shows "\name := definition" with an editable definition. */
export class MacroView implements NodeView {
  dom: HTMLElement;
  field: LyxMathField;
  nameEl: HTMLElement;
  private updating = false;
  private lastDef: string;

  constructor(private node: PMNode, private view: EditorView, private getPos: () => number | undefined) {
    this.dom = document.createElement('span');
    this.dom.className = 'lyx-macro';
    applyChangeAttrs(this.dom, node);
    this.nameEl = document.createElement('span');
    this.nameEl.className = 'macro-name';
    this.nameEl.contentEditable = 'false';
    const def = this.parse();
    this.nameEl.textContent = '\\' + (def?.name ?? '?') + (def && def.args ? `[${def.args}]` : '') + ' ≔ ';
    this.lastDef = def?.def ?? '';
    const { key, table } = macroTableFor(view, getPos());
    this.field = new LyxMathField({
      latex: '$' + this.lastDef + '$', display: false, macros: table,
      imageContext: { project: viewProject(this.view), docDir: viewDocDir(this.view) },
      onChange: latex => this.commit(latex.replace(/^\$|\$$/g, '')),
      onMoveOut: (dir, o) => moveOut(this.view, this.getPos, dir, !!o.insertSpace),
      onDragOut: (ev, reenter) => dragOutOf(this.view, this.getPos, ev, reenter),
      onShiftClick: ev => shiftClickAt(this.view, ev.clientX, ev.clientY),
      onSelectOut: dir => selectOutOf(this.view, this.getPos, dir),
    });
    (this.field as any)._macroKey = key;
    this.dom.append(this.nameEl, this.field.dom);
    mathViews.add(this);
    if (def?.display) { const d = document.createElement('span'); d.className = 'macro-display'; d.textContent = ' (shown as: ' + def.display + ')'; d.contentEditable = 'false'; this.dom.append(d); }
    wire(this.field, () => [{ label: 'Math macro definition', info: true }, { label: 'Delete macro definition', action: () => deleteFormula(this.view, this.getPos) }], () => selectedFormulaItems(this.view, this.getPos, this.field));
    this.nameEl.addEventListener('mousedown', (ev) => { ev.preventDefault(); const pos = this.getPos(); if (pos !== undefined) { this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, pos))); this.view.focus(); } });
  }
  private parse() { try { return macroFromLyxLines(JSON.parse(this.node.attrs.lines)); } catch { return null; } }
  private commit(latex: string) {
    if (this.updating || latex === this.lastDef) return;
    const pos = this.getPos();
    if (pos === undefined) return;
    const cur = this.view.state.doc.nodeAt(pos);
    if (!cur) return;
    const lines: string[] = JSON.parse(cur.attrs.lines);
    const d = macroFromLyxLines(lines);
    if (!d) return;
    const cmd = /^\\renewcommand/.test(lines[0]) ? '\\renewcommand' : '\\newcommand';
    lines[0] = `${cmd}{\\${d.name}}${d.args ? `[${d.args}]` : ''}{${latex}}`;
    this.lastDef = latex;
    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, undefined, { ...cur.attrs, lines: JSON.stringify(lines) }));
  }
  refreshMacros() { const { key, table } = macroTableFor(this.view, undefined); this.field.setMacros(table, key); }
  ensureField(): LyxMathField { return this.field; }
  update(node: PMNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    applyChangeAttrs(this.dom, node);
    const def = this.parse();
    if (def && def.def !== this.lastDef && !this.field.hasFocus()) { this.updating = true; this.lastDef = def.def; this.field.setLatex('$' + def.def + '$'); this.updating = false; }
    return true;
  }
  selectNode() { this.dom.classList.add('ProseMirror-selectednode'); }
  deselectNode() { this.dom.classList.remove('ProseMirror-selectednode'); }
  stopEvent(ev: Event) { return this.field.dom.contains(ev.target as Node); }
  ignoreMutation() { return true; }
  focus(where: 'start' | 'end' = 'end') { this.field.focus(where); }
  destroy() { mathViews.delete(this); this.field.destroy(); }
}

export { activeMathField };
